import { Lock, Star, Coffee, LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Progress } from "@/components/ui/progress";
import { badgeImageMap } from './BadgeImages';

// Soft, warm accent colors per category — matching app's cream/brown aesthetic
const categoryAccent: Record<string, {
  bg: string;
  bgEnd: string;
  ring: string;
  glow: string;
  progressTrack: string;
  progressFill: string;
}> = {
  exploration: {
    bg: '#c4a882',
    bgEnd: '#d4b892',
    ring: '#b49872',
    glow: '0 0 8px 1px rgba(196,168,130,0.3)',
    progressTrack: 'bg-[#c4a882]/20',
    progressFill: '[&>div]:bg-[#c4a882]',
  },
  work: {
    bg: '#C4956A',
    bgEnd: '#D4A57A',
    ring: '#B4855A',
    glow: '0 0 8px 1px rgba(196,149,106,0.3)',
    progressTrack: 'bg-[#C4956A]/20',
    progressFill: '[&>div]:bg-[#C4956A]',
  },
  community: {
    bg: '#D4A09A',
    bgEnd: '#E0B0AA',
    ring: '#C4908A',
    glow: '0 0 8px 1px rgba(212,160,154,0.3)',
    progressTrack: 'bg-[#D4A09A]/20',
    progressFill: '[&>div]:bg-[#D4A09A]',
  },
  fun: {
    bg: '#D4B483',
    bgEnd: '#E0C493',
    ring: '#C4A473',
    glow: '0 0 8px 1px rgba(212,180,131,0.3)',
    progressTrack: 'bg-[#D4B483]/20',
    progressFill: '[&>div]:bg-[#D4B483]',
  },
};

interface BadgeIconProps {
  iconName: string;
  slug?: string;
  name: string;
  description: string;
  category: string;
  isEarned: boolean;
  size?: 'sm' | 'md' | 'lg';
  showTooltip?: boolean;
  earnedAt?: string;
  progress?: {
    current: number;
    threshold: number;
    percentage: number;
  };
}

export function BadgeIcon({ 
  iconName, slug, name, description, category, isEarned, 
  size = 'md', showTooltip = true, earnedAt, progress 
}: BadgeIconProps) {
  const badgeImage = slug ? badgeImageMap[slug] : undefined;
  const accent = categoryAccent[category] || categoryAccent.exploration;
  
  const sizeMap = {
    sm: { outer: 28, inner: 22, ring: 2, imgSize: 20 },
    md: { outer: 56, inner: 44, ring: 3, imgSize: 38 },
    lg: { outer: 72, inner: 58, ring: 3.5, imgSize: 50 },
  };
  const s = sizeMap[size];

  const badgeContent = (
    <div className="relative" style={{ width: s.outer, height: s.outer }}>
      <svg
        width={s.outer}
        height={s.outer}
        viewBox={`0 0 ${s.outer} ${s.outer}`}
        className="block"
      >
        <defs>
          <radialGradient id={`badge-fill-${slug || iconName}-${category}`} cx="35%" cy="30%">
            <stop offset="0%" stopColor={accent.bgEnd} />
            <stop offset="100%" stopColor={accent.bg} />
          </radialGradient>
          <radialGradient id={`badge-locked-${slug || iconName}-${category}`} cx="35%" cy="30%">
            <stop offset="0%" stopColor="#d5d0c8" />
            <stop offset="100%" stopColor="#b8b3aa" />
          </radialGradient>
          <radialGradient id={`badge-shine-${slug || iconName}-${category}`} cx="30%" cy="20%" r="50%">
            <stop offset="0%" stopColor="rgba(255,248,220,0.4)" />
            <stop offset="100%" stopColor="rgba(255,248,220,0)" />
          </radialGradient>
          <clipPath id={`badge-clip-${slug || iconName}-${category}`}>
            <circle cx={s.outer / 2} cy={s.outer / 2} r={s.inner / 2} />
          </clipPath>
        </defs>

        {/* Outer ring */}
        <circle
          cx={s.outer / 2}
          cy={s.outer / 2}
          r={s.outer / 2 - s.ring / 2}
          fill="none"
          stroke={isEarned ? accent.ring : '#c4bfb6'}
          strokeWidth={s.ring}
          opacity={isEarned ? 1 : 0.5}
        />

        {/* Inner filled circle background */}
        <circle
          cx={s.outer / 2}
          cy={s.outer / 2}
          r={s.inner / 2}
          fill={isEarned 
            ? `url(#badge-fill-${slug || iconName}-${category})` 
            : `url(#badge-locked-${slug || iconName}-${category})`
          }
        />

        {/* Shine overlay for earned */}
        {isEarned && (
          <circle
            cx={s.outer / 2}
            cy={s.outer / 2}
            r={s.inner / 2}
            fill={`url(#badge-shine-${slug || iconName}-${category})`}
          />
        )}

        {/* Progress ring for locked badges */}
        {!isEarned && progress && progress.percentage > 0 && size !== 'sm' && (
          <circle
            cx={s.outer / 2}
            cy={s.outer / 2}
            r={s.outer / 2 - s.ring / 2}
            fill="none"
            stroke={accent.bg}
            strokeWidth={2}
            strokeDasharray={`${(progress.percentage / 100) * Math.PI * (s.outer - s.ring)} ${Math.PI * (s.outer - s.ring)}`}
            strokeLinecap="round"
            transform={`rotate(-90 ${s.outer / 2} ${s.outer / 2})`}
            opacity={0.8}
          />
        )}
      </svg>

      {/* Badge image centered on top of SVG */}
      <div className="absolute inset-0 flex items-center justify-center">
        {badgeImage ? (
          <img
            src={badgeImage}
            alt={name}
            width={s.imgSize}
            height={s.imgSize}
            className={cn(
              "rounded-full object-cover drop-shadow-sm",
              !isEarned && "grayscale opacity-50"
            )}
            style={{ width: s.imgSize, height: s.imgSize }}
          />
        ) : (
          <Coffee
            size={s.imgSize * 0.6}
            strokeWidth={2}
            className={cn(
              "drop-shadow-sm",
              isEarned ? "text-white" : "text-[#8a847c] opacity-50"
            )}
          />
        )}
      </div>

      {/* Locked overlay for unearned */}
      {!isEarned && size !== 'sm' && (
        <div className="absolute bottom-0 right-0 rounded-full bg-[#e8e3db] border border-[#d5d0c8] flex items-center justify-center"
          style={{ 
            width: size === 'lg' ? 20 : 16, 
            height: size === 'lg' ? 20 : 16,
          }}
        >
          <Lock size={size === 'lg' ? 10 : 8} className="text-[#9a948b]" />
        </div>
      )}

      {/* Glow effect for earned badges */}
      {isEarned && (
        <div
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{ boxShadow: accent.glow }}
        />
      )}
    </div>
  );

  if (!showTooltip) return badgeContent;

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          {badgeContent}
        </TooltipTrigger>
        <TooltipContent 
          side="top" 
          className="max-w-[220px] bg-[#2d1810] text-[#f5f0e8] border-[#5c3c2b]"
        >
          <div className="space-y-2">
            <p className="font-display font-semibold text-sm">{name}</p>
            <p className="text-xs text-[#c4bfb6]">{description}</p>
            {isEarned && earnedAt && (
              <p className="text-[10px] text-[#d4af37] pt-1 flex items-center gap-1">
                <Star size={9} className="fill-current" />
                Earned {new Date(earnedAt).toLocaleDateString()}
              </p>
            )}
            {!isEarned && progress && (
              <div className="space-y-1.5 pt-1">
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-[#c4bfb6]">Progress</span>
                  <span className="font-medium text-[#f5f0e8]">{progress.current}/{progress.threshold}</span>
                </div>
                <Progress 
                  value={progress.percentage} 
                  className={cn("h-1.5 bg-[#5c3c2b]/50", accent.progressFill)}
                />
              </div>
            )}
            {!isEarned && !progress && (
              <p className="text-[10px] text-[#9a948b] pt-1 italic flex items-center gap-1">
                <Lock size={8} />
                Not yet earned
              </p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
